import { isKeptThreadItem } from '@/lib/session-conversation';
import type { ExperienceBlock, ExperiencePage, ExperienceTheme } from '@matter_extractor/shared';
import { FIRST_TEMPS_START, conversationItemsOf, isBlockDisplayed } from '@matter_extractor/shared';
import { type ReactNode, useEffect, useRef } from 'react';
import {
  type PageReaderProps,
  READER_TEST_IDS,
  type ReaderAdvance,
  type ReaderFormat,
  type ReaderNoyau,
  type ReaderValue,
  type ReaderValues,
} from './contracts';
import { ReaderBody } from './reader-body';
import { EVERY_BLOCK_SHOWN } from './reader-play';
import './reader.css';

export interface ConversationItemsProps {
  /** The page of the thread whose elements these are. */
  readonly page: ExperiencePage;
  readonly theme: ExperienceTheme;
  readonly format: ReaderFormat;
  /**
   * `live` - every element the page shows in the thread, clickable (the page the
   * person is on). `kept` - only what stays once the page is answered: the
   * portrait, a link, a copyable text.
   */
  readonly mode: 'live' | 'kept';
  /**
   * The determination of the noyau is not rendered yet: two GHOST cards stand
   * where the cards will be, and nothing says a word (prolongation.md §5, P8).
   */
  readonly waitingForCards: boolean;
  readonly values: ReaderValues;
  readonly onValueChange: (blockId: string, value: ReaderValue) => void;
  readonly onAdvance: (advance: ReaderAdvance) => void;
  readonly resolveVariable: PageReaderProps['resolveVariable'];
  readonly conditions: PageReaderProps['conditions'];
  readonly noyau: ReaderNoyau | undefined;
  /** The Bouton whose advance is in flight says its waiting label (#174). */
  readonly waitingBlockId: string | null;
  /** « Réduire les animations » (§9.4): the ghosts do not pulse. */
  readonly reducedMotion: boolean;
}

/**
 * The elements of a page played in the conversation, under its bubble
 * (prolongation.md §3.3, §5; planche `lecteur.html`).
 *
 * ## The same reader, in the thread
 *
 * Not a second painter of the blocks: the page's elements go through
 * `ReaderBody`, the component the page reader and the editor previews mount, so
 * a card, a Bouton, a Note and a copyable text look and behave as they do on a
 * page - « le rendu doit être le même ». What changes is the frame around them.
 * The wrapper carries the `lecteur` class and its data attributes, so the rules
 * of the stylesheet that need an ancestor (the cards side by side on a screen,
 * the reduced motion) still apply, and `.lecteur-fil` takes the page's own
 * background, veil and scroll off it: the window is painted once, by the page
 * reader under the conversation.
 *
 * ## What is drawn, and in which order
 *
 * `conversationItemsOf` of `shared` - the page's elements, its temps ignored, a
 * Texte, an Image and a Son left out because the bubble replaces them - with the
 * display conditions of §9.2 applied as the page reader applies them. One temps
 * holds them all and every one is shown at once: « tout ce qui se clique arrive
 * d'un coup sous la bulle ».
 *
 * ## The first element takes the focus
 *
 * « Le focus va au premier élément de la page quand il arrive »: the elements
 * mount when the bubble is complete, and the person who reads with a keyboard
 * finds the first control there rather than in the field they no longer have.
 */
export function ConversationItems({
  page,
  theme,
  format,
  mode,
  waitingForCards,
  values,
  onValueChange,
  onAdvance,
  resolveVariable,
  conditions,
  noyau,
  waitingBlockId,
  reducedMotion,
}: ConversationItemsProps): ReactNode {
  const root = useRef<HTMLDivElement | null>(null);
  const live = mode === 'live';
  // The cards are replaced by their ghosts for as long as they are not dealt.
  const ghosts = live && waitingForCards;

  useEffect(() => {
    if (!live || ghosts) return;
    root.current
      ?.querySelector<HTMLElement>(
        'input:not([disabled]), button:not([disabled]), a[href], textarea',
      )
      ?.focus({ preventScroll: true });
  }, [live, ghosts]);

  const displayed =
    conditions === undefined
      ? EVERY_BLOCK_SHOWN
      : (block: ExperienceBlock) => isBlockDisplayed(block, conditions);
  const blocks = itemsOf(page, mode, ghosts, displayed);
  if (blocks.length === 0 && !ghosts) return null;

  // One temps holding every element: the temps of a page of the thread are ignored.
  const flat: ExperiencePage = {
    ...page,
    autoDurationSeconds: null,
    temps: [{ id: `fil-${page.id}`, speed: 'normal', start: FIRST_TEMPS_START, blocks }],
  };

  return (
    <div
      ref={root}
      className="lecteur lecteur-fil"
      data-testid={READER_TEST_IDS.threadItems}
      data-thread-mode={mode}
      data-format={format}
      data-motion={reducedMotion ? 'reduced' : 'full'}
      data-play="off"
    >
      {ghosts ? <GhostCards /> : null}
      {blocks.length === 0 ? null : (
        <ReaderBody
          page={flat}
          theme={theme}
          mode="interactive"
          motion={reducedMotion ? 'reduced' : 'full'}
          play={false}
          gestured={false}
          values={values}
          selectedBlockId={null}
          onSelectBlock={undefined}
          onValueChange={onValueChange}
          onAdvance={onAdvance}
          resolveVariable={resolveVariable}
          variableNames={undefined}
          revealed={undefined}
          onRevealed={undefined}
          conditions={conditions}
          conditionMark={undefined}
          noyau={noyau}
          waitingBlockId={waitingBlockId}
        />
      )}
    </div>
  );
}

/**
 * The blocks to draw: the page's elements that its conditions let through, all
 * of them while it is live and the ones that stay once it is answered. While the
 * cards are awaited, the Noyau block of choice is not drawn: its ghosts are.
 */
function itemsOf(
  page: ExperiencePage,
  mode: 'live' | 'kept',
  ghosts: boolean,
  displayed: (block: ExperienceBlock) => boolean,
): ExperienceBlock[] {
  return conversationItemsOf(page).filter((block) => {
    if (!displayed(block)) return false;
    if (mode === 'kept') return isKeptThreadItem(block);
    return !(ghosts && block.type === 'noyau' && block.mode !== 'portrait');
  });
}

/**
 * Two ghost cards, at the exact place and with the shape of the cards - the
 * `lecteur-noyau-cartes` grid and the `lecteur-noyau-carte` box - pulsing
 * softly. No text, not a word: they say « something arrives here » without
 * putting the wait on the interviewer (planche `lecteur.html`, section 2).
 * Hidden from assistive technology: there is nothing to read in them.
 */
function GhostCards(): ReactNode {
  return (
    <div className="lecteur-noyau-cartes" aria-hidden="true">
      {[0, 1].map((rank) => (
        <div
          key={rank}
          className="lecteur-noyau-carte lecteur-fantome"
          data-testid={READER_TEST_IDS.threadGhost}
        />
      ))}
    </div>
  );
}
